import { useState, useEffect } from 'react';
import { Layout } from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { User, Shield, Database, Bell, Receipt, Building2, Phone, Mail, Globe, CreditCard, Save, FileText } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/utils/api';
import { clearAppCache } from '@/utils/cacheUtils';

export default function Settings({ user, onLogout }) {
  const [profile, setProfile] = useState({
    full_name: user.full_name,
    email: user.email,
    phone_number: ''
  });
  const [savingProfile, setSavingProfile] = useState(false);

  const [billingSettings, setBillingSettings] = useState({
    company_name: 'Ganesa Homestate',
    company_tagline: 'Your Trusted Property Partner',
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    pincode: '',
    phone: '',
    email: '',
    website: '',
    gst_number: '',
    pan_number: '',
    bank_name: '',
    account_number: '',
    ifsc_code: '',
    upi_id: '',
    terms_and_conditions: ''
  });

  const [loadingBilling, setLoadingBilling] = useState(true);
  const [savingBilling, setSavingBilling] = useState(false);

  useEffect(() => {
    fetchBillingSettings();
    if (user.role === 'admin') {
      api.get('/auth/admin-phone-status').then(res => {
        if (res.data.phone_number) setProfile(p => ({ ...p, phone_number: res.data.phone_number }));
      }).catch(() => {});
    }
  }, [user.role]);

  const fetchBillingSettings = async () => {
    try {
      const response = await api.get('/billing-settings');
      if (response.data.settings) {
        setBillingSettings(prev => ({
          ...prev,
          ...response.data.settings
        }));
      }
    } catch (error) {
      console.error('Failed to load billing settings');
    } finally {
      setLoadingBilling(false);
    }
  };

  const handleSaveProfile = async () => {
    setSavingProfile(true);
    try {
      if (user.role === 'admin' && profile.phone_number) {
        await api.post('/auth/set-admin-phone', { phone_number: profile.phone_number });
      }
      await clearAppCache();
      toast.success('Profile settings saved!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save profile');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleSaveBillingSettings = async () => {
    setSavingBilling(true);
    try {
      await api.put('/billing-settings', billingSettings);
      await clearAppCache();
      toast.success('Billing settings saved successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save billing settings');
    } finally {
      setSavingBilling(false);
    }
  };

  const isAdmin = user.role === 'admin';

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-6 max-w-5xl mx-auto" data-testid="settings-page">
        <Card>
          <CardHeader>
            <CardTitle className="text-2xl">Settings</CardTitle>
            <CardDescription>Manage your account and system settings</CardDescription>
          </CardHeader>
        </Card>

        <Tabs defaultValue="profile" className="space-y-4">
          <TabsList className="grid w-full grid-cols-4 h-auto p-1">
            <TabsTrigger value="profile" className="gap-2 py-2">
              <User size={16} />
              <span className="hidden sm:inline">Profile</span>
            </TabsTrigger>
            <TabsTrigger value="role" className="gap-2 py-2">
              <Shield size={16} />
              <span className="hidden sm:inline">Role</span>
            </TabsTrigger>
            {isAdmin && (
              <TabsTrigger value="billing" className="gap-2 py-2" data-testid="billing-settings-tab">
                <Receipt size={16} />
                <span className="hidden sm:inline">Invoice</span>
              </TabsTrigger>
            )}
            <TabsTrigger value="notifications" className="gap-2 py-2">
              <Bell size={16} />
              <span className="hidden sm:inline">Alerts</span>
            </TabsTrigger>
          </TabsList>

          {/* Profile Tab */}
          <TabsContent value="profile">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="text-emerald-600" />
                  Profile Settings
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Full Name</Label>
                    <Input
                      value={profile.full_name}
                      onChange={(e) => setProfile({ ...profile, full_name: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Email</Label>
                    <Input
                      type="email"
                      value={profile.email}
                      onChange={(e) => setProfile({ ...profile, email: e.target.value })}
                    />
                  </div>
                </div>
                {isAdmin && (
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Phone size={14} className="text-emerald-600" />
                      Phone Number (OTP Login ke liye)
                    </Label>
                    <Input
                      data-testid="admin-phone-input"
                      value={profile.phone_number}
                      onChange={(e) => setProfile({ ...profile, phone_number: e.target.value })}
                      placeholder="919876543210 (country code ke saath)"
                    />
                    <p className="text-xs text-slate-500">Yeh number OTP login me use hoga. Country code ke saath daale (jaise: 919876543210)</p>
                  </div>
                )}
                <Button onClick={handleSaveProfile} disabled={savingProfile} className="bg-emerald-600 hover:bg-emerald-700">
                  {savingProfile ? 'Saving...' : 'Save Changes'}
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Role Tab */}
          <TabsContent value="role">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="text-emerald-600" />
                  Role & Permissions
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <p className="text-sm text-slate-600">Current Role</p>
                    <p className="font-semibold text-lg capitalize mt-1">{user.role}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-600 mb-2">Permissions</p>
                    <div className="flex flex-wrap gap-2">
                      {user.permissions && user.permissions.length > 0 ? (
                        user.permissions.map((permission, index) => (
                          <span
                            key={index}
                            className="px-3 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700 border border-emerald-200"
                          >
                            {permission}
                          </span>
                        ))
                      ) : (
                        <span className="text-sm text-slate-500">No specific permissions</span>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Billing/Invoice Settings Tab - Admin Only */}
          {isAdmin && (
            <TabsContent value="billing">
              <div className="space-y-6">
                {/* Company Information */}
                <Card data-testid="billing-company-card">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Building2 className="text-emerald-600" />
                      Company Information
                    </CardTitle>
                    <CardDescription>Ye details invoice header mein dikhenge</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Company Name *</Label>
                        <Input
                          data-testid="company-name-input"
                          value={billingSettings.company_name}
                          onChange={(e) => setBillingSettings({ ...billingSettings, company_name: e.target.value })}
                          placeholder="Ganesa Homestate"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Company Tagline</Label>
                        <Input
                          value={billingSettings.company_tagline}
                          onChange={(e) => setBillingSettings({ ...billingSettings, company_tagline: e.target.value })}
                          placeholder="Your Trusted Property Partner"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Address Line 1</Label>
                      <Input
                        value={billingSettings.address_line1}
                        onChange={(e) => setBillingSettings({ ...billingSettings, address_line1: e.target.value })}
                        placeholder="123 Main Street"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Address Line 2</Label>
                      <Input
                        value={billingSettings.address_line2}
                        onChange={(e) => setBillingSettings({ ...billingSettings, address_line2: e.target.value })}
                        placeholder="Near XYZ Building"
                      />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label>City</Label>
                        <Input
                          value={billingSettings.city}
                          onChange={(e) => setBillingSettings({ ...billingSettings, city: e.target.value })}
                          placeholder="Mumbai"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>State</Label>
                        <Input
                          value={billingSettings.state}
                          onChange={(e) => setBillingSettings({ ...billingSettings, state: e.target.value })}
                          placeholder="Maharashtra"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Pincode</Label>
                        <Input
                          value={billingSettings.pincode}
                          onChange={(e) => setBillingSettings({ ...billingSettings, pincode: e.target.value })}
                          placeholder="400001"
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Contact Information */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Phone className="text-emerald-600" />
                      Contact Information
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label className="flex items-center gap-2">
                          <Phone size={14} /> Phone
                        </Label>
                        <Input
                          data-testid="phone-input"
                          value={billingSettings.phone}
                          onChange={(e) => setBillingSettings({ ...billingSettings, phone: e.target.value })}
                          placeholder="+91 98765 43210"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="flex items-center gap-2">
                          <Mail size={14} /> Email
                        </Label>
                        <Input
                          type="email"
                          value={billingSettings.email}
                          onChange={(e) => setBillingSettings({ ...billingSettings, email: e.target.value })}
                          placeholder="info@ganesa.com"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="flex items-center gap-2">
                          <Globe size={14} /> Website
                        </Label>
                        <Input
                          value={billingSettings.website}
                          onChange={(e) => setBillingSettings({ ...billingSettings, website: e.target.value })}
                          placeholder="www.ganesa.com"
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Tax & Legal */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <FileText className="text-emerald-600" />
                      Tax & Legal Information
                    </CardTitle>
                    <CardDescription>GST aur PAN details invoice mein print honge</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>GST Number</Label>
                        <Input
                          data-testid="gst-input"
                          value={billingSettings.gst_number}
                          onChange={(e) => setBillingSettings({ ...billingSettings, gst_number: e.target.value })}
                          placeholder="27AABCU9603R1ZM"
                          className="font-mono"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>PAN Number</Label>
                        <Input
                          value={billingSettings.pan_number}
                          onChange={(e) => setBillingSettings({ ...billingSettings, pan_number: e.target.value })}
                          placeholder="AABCU9603R"
                          className="font-mono"
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Bank Details */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <CreditCard className="text-emerald-600" />
                      Bank Details
                    </CardTitle>
                    <CardDescription>Payment details jo invoice mein dikhenge</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Bank Name</Label>
                        <Input
                          value={billingSettings.bank_name}
                          onChange={(e) => setBillingSettings({ ...billingSettings, bank_name: e.target.value })}
                          placeholder="HDFC Bank"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Account Number</Label>
                        <Input
                          value={billingSettings.account_number}
                          onChange={(e) => setBillingSettings({ ...billingSettings, account_number: e.target.value })}
                          placeholder="1234567890123"
                          className="font-mono"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>IFSC Code</Label>
                        <Input
                          value={billingSettings.ifsc_code}
                          onChange={(e) => setBillingSettings({ ...billingSettings, ifsc_code: e.target.value })}
                          placeholder="HDFC0001234"
                          className="font-mono"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>UPI ID</Label>
                        <Input
                          value={billingSettings.upi_id}
                          onChange={(e) => setBillingSettings({ ...billingSettings, upi_id: e.target.value })}
                          placeholder="ganesa@upi"
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Terms & Conditions */}
                <Card>
                  <CardHeader>
                    <CardTitle>Terms & Conditions</CardTitle>
                    <CardDescription>Ye invoice ke end mein print honge - har line ek new term hogi</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <textarea
                      rows={8}
                      value={billingSettings.terms_and_conditions}
                      onChange={(e) => setBillingSettings({ ...billingSettings, terms_and_conditions: e.target.value })}
                      className="w-full px-3 py-2 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm"
                      placeholder="1. All payments are non-refundable.&#10;2. This invoice is valid for 30 days.&#10;3. Registration charges extra as applicable.&#10;4. Possession will be given after full payment.&#10;5. For queries, contact our office."
                      data-testid="terms-textarea"
                    />
                  </CardContent>
                </Card>

                {/* Save Button */}
                <Button
                  onClick={handleSaveBillingSettings}
                  disabled={savingBilling}
                  data-testid="save-billing-settings-btn"
                  className="w-full bg-emerald-600 hover:bg-emerald-700 h-12 gap-2"
                >
                  {savingBilling ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save size={18} />
                      Save Invoice Settings
                    </>
                  )}
                </Button>
              </div>
            </TabsContent>
          )}

          {/* Notifications Tab */}
          <TabsContent value="notifications">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Bell className="text-emerald-600" />
                  Notification Preferences
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">Email Notifications</p>
                      <p className="text-sm text-slate-600">Receive updates via email</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input type="checkbox" defaultChecked className="sr-only peer" />
                      <div className="w-11 h-6 bg-slate-200 peer-focus:ring-4 peer-focus:ring-emerald-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-600"></div>
                    </label>
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">Payment Reminders</p>
                      <p className="text-sm text-slate-600">Get notified about due payments</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input type="checkbox" defaultChecked className="sr-only peer" />
                      <div className="w-11 h-6 bg-slate-200 peer-focus:ring-4 peer-focus:ring-emerald-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-600"></div>
                    </label>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}
